<script lang="ts">
import {
  type TrustpilotStats,
  TrustpilotStorage,
} from '@/services/trustpilot-storage'
import { Themes } from '@/utils/helper-objects'
import { defineComponent } from 'vue'

/**
 * Trustpilot component.
 *
 * The Trustpilot-provided Javascript is **not** reactive and therefore
 * requires a page reload for any of this component prop's to take effect.
 */
export default defineComponent({
  props: {
    /**
     * Whether to allow the widget to be linked to reviews.
     */
    linkToReviews: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to display stats (total reviews, rating, etc.)
     */
    showStats: {
      type: Boolean,
      default: true,
    },
    /**
     * Size of the Trustpilot widget.
     * Changes the sizing of the text and stars.
     *
     * Options: sm, md, lg
     */
    size: {
      type: String,
      default: 'md',
      validator(value: string) {
        return ['sm', 'md', 'lg'].includes(value)
      },
    },
  },
  setup() {
    return { Themes }
  },
  data() {
    return {
      stats: null as TrustpilotStats | null,
      isLoadingStats: false,
    }
  },
  mounted() {
    void this.loadStats()
  },
  methods: {
    async loadStats() {
      this.isLoadingStats = true
      this.stats = await TrustpilotStorage.getStats()
      this.isLoadingStats = false
    },
  },
})
</script>

<template>
  <!-- Show stats if enabled, otherwise show widget -->
  <div class="w-full trustpilot-widget">
    <div v-if="isLoadingStats" class="opacity-60 text-center text-sm">
      Loading...
    </div>

    <div
      v-else-if="stats"
      class="flex flex-col text-white gap-2"
      :class="`trustpilot-widget--${size}`"
    >
      <div class="flex items-center justify-center gap-2">
        <span class="text-xs @xs/main:text-md font-bold leading-none"
          >Excellent</span
        >
        <img
          src="@/assets/images/trustpilot-stars.svg"
          alt="Trustpilot 4.8 stars"
          class="h-[17px] w-auto trustpilot-widget__stars"
        />
      </div>

      <div class="flex items-center justify-center whitespace-nowrap gap-x-1">
        <span
          class="text-[0.625rem] @xs/main:text-xs font-bold underline leading-none"
        >
          {{ stats.total_reviews.toLocaleString() }} reviews on</span
        ><img
          src="@/assets/images/trustpilot-logo.svg"
          alt="Trustpilot"
          class="h-[16px] w-[65px] trustpilot-widget__logo"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trustpilot-widget--sm {
  @apply text-xs leading-none;

  img {
    @apply h-3;
  }
}
</style>
